import { formatMobilePhoneInput, formatTel } from '@/functions/format-phone';
import React from 'react';

interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  type?: 'mobile' | 'landline';
  required?: boolean;
  className?: string;
  error?: string;
  // react-hook-form 지원
  register?: {
    name: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    ref: React.Ref<HTMLInputElement>;
  };
}

export function PhoneInput({
  label,
  value = '',
  onChange,
  type = 'mobile',
  required = false,
  className = '',
  error,
  register,
  ...props
}: PhoneInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formatted =
      type === 'mobile'
        ? formatMobilePhoneInput(inputValue)
        : formatTel(inputValue);

    if (register) {
      // react-hook-form 사용 시: 포맷팅된 값으로 직접 업데이트
      e.target.value = formatted;
      register.onChange(e);
    } else {
      // 일반 사용 시
      onChange?.(formatted);
    }
  };

  // 초기 값도 포맷팅 적용
  const formattedValue = React.useMemo(() => {
    if (!value) return '';
    return type === 'mobile' ? formatMobilePhoneInput(value) : formatTel(value);
  }, [value, type]);

  const placeholder = type === 'mobile' ? '010-0000-0000' : '02-0000-0000';

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">(필수)</span>}
      </label>
      <div className="relative">
        {register ? (
          <input
            type="tel"
            className={`w-full px-4 py-3 border ${
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
            } rounded-xl focus:ring-2 transition-all text-sm`}
            placeholder={placeholder}
            required={required}
            {...register}
            {...props}
            onChange={handleChange}
          />
        ) : (
          <input
            type="tel"
            value={formattedValue}
            onChange={handleChange}
            className={`w-full px-4 py-3 border ${
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
            } rounded-xl focus:ring-2 transition-all text-sm`}
            placeholder={placeholder}
            required={required}
            {...props}
          />
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
